// ErrorBoundary uses class component (required for componentDidCatch),
// so we use @lingui/core/macro's standalone t instead of useLingui() hook.
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Button, Result, Typography } from 'antd'
import { Component, type ErrorInfo, type ReactNode } from 'react'

const { Paragraph, Text } = Typography

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo)
    this.setState({ errorInfo })
  }

  private handleReload = () => {
    window.location.reload()
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--ant-color-bg-container)',
          }}
        >
          <Result
            status="error"
            title={t`Something went wrong`}
            subTitle={t`The application encountered an unexpected error. Please try reloading the page.`}
            extra={[
              <Button type="primary" key="reload" onClick={this.handleReload}>
                <Trans>Reload Page</Trans>
              </Button>,
            ]}
          >
            <div className="desc">
              <Paragraph>
                <Text strong style={{ fontSize: 16 }}>
                  <Trans>Error Details:</Trans>
                </Text>
              </Paragraph>
              <Paragraph>
                <Text type="danger">{this.state.error?.toString()}</Text>
              </Paragraph>
              {this.state.errorInfo && (
                <pre
                  style={{
                    maxWidth: '800px',
                    overflow: 'auto',
                    padding: '12px',
                    background: 'var(--ant-color-fill-tertiary)',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                >
                  {this.state.errorInfo.componentStack}
                </pre>
              )}
            </div>
          </Result>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
